import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Constants } from '../../lib/const/constants';

const GOOGLE_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const GOOGLE_PLACE_DETAILS_URL = 'https://places.googleapis.com/v1/places/';
const PLACE_DETAILS_FIELDS = 'formattedAddress,addressComponents';

const generateSessionToken = () => {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const getComponent = (components, type, useShortText = false) => {
  const match = components.find((component) => component.types?.includes(type));
  if (!match) return '';
  return (useShortText ? match.shortText : match.longText) || '';
};

const parsePlaceDetails = (place) => {
  const components = place.addressComponents || [];
  const street = [
    getComponent(components, 'street_number'),
    getComponent(components, 'route'),
  ]
    .filter(Boolean)
    .join(' ');
  // Forms have no separate flat / building field, so keep them in the address line
  const addressLine1 = [
    getComponent(components, 'subpremise'),
    getComponent(components, 'premise'),
    street,
  ]
    .filter(Boolean)
    .join(', ');

  return {
    formattedAddress: place.formattedAddress || '',
    addressLine1,
    city:
      getComponent(components, 'postal_town') ||
      getComponent(components, 'locality') ||
      getComponent(components, 'administrative_area_level_2'),
    postcode: getComponent(components, 'postal_code'),
    country: getComponent(components, 'country'),
    countryCode: getComponent(components, 'country', true),
  };
};

const dropdownStyle = {
  position: 'absolute',
  zIndex: 20,
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  maxHeight: '240px',
  overflowY: 'auto',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 10px 15px rgba(0, 0, 0, 0.1)',
  listStyle: 'none',
  padding: 0,
  textAlign: 'left',
};

const itemStyle = {
  padding: '8px 12px',
  cursor: 'pointer',
  color: '#252528',
};

function AddressSearchInput({
  value,
  onChange,
  placeholder = 'Address',
  className,
  style,
  required,
  onBlur,
  onPlaceSelected,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const latestRequestRef = useRef(0);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchText = (value || '').trim();
    if (!open || searchText.length < 3) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current = generateSessionToken();
      }
      const requestId = ++latestRequestRef.current;
      axios
        .post(
          GOOGLE_AUTOCOMPLETE_URL,
          { input: searchText, sessionToken: sessionTokenRef.current, languageCode: 'en' },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Goog-Api-Key': Constants.GOOGLE_MAPS_API_KEY,
            },
          },
        )
        .then((resp) => {
          if (requestId !== latestRequestRef.current) return;
          setSuggestions(resp.data.suggestions || []);
        })
        .catch((err) => {
          console.log('Google address search error', err.response?.data || err);
          if (requestId !== latestRequestRef.current) return;
          setSuggestions([]);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [value, open]);

  const handleSelect = (suggestion) => {
    const { placeId, text } = suggestion.placePrediction;
    const sessionToken = sessionTokenRef.current;
    sessionTokenRef.current = null;
    const requestId = ++latestRequestRef.current;

    onChange(text.text);
    setSuggestions([]);
    setOpen(false);
    axios
      .get(`${GOOGLE_PLACE_DETAILS_URL}${encodeURIComponent(placeId)}`, {
        params: sessionToken ? { sessionToken, languageCode: 'en' } : { languageCode: 'en' },
        headers: {
          'X-Goog-Api-Key': Constants.GOOGLE_MAPS_API_KEY,
          'X-Goog-FieldMask': PLACE_DETAILS_FIELDS,
        },
      })
      .then((resp) => {
        // User edited the address after picking; don't overwrite their changes
        if (requestId !== latestRequestRef.current) return;
        if (onPlaceSelected) {
          onPlaceSelected(parsePlaceDetails(resp.data));
        }
      })
      .catch((err) => {
        console.log('Google place details error', err.response?.data || err);
      });
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
      <input
        type="text"
        autoComplete="off"
        required={required}
        placeholder={placeholder}
        value={value || ''}
        className={className}
        style={style}
        onBlur={(e) => {
          setOpen(false);
          if (onBlur) onBlur(e);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        onChange={(e) => {
          latestRequestRef.current++;
          onChange(e.target.value);
          setOpen(true);
        }}
      />
      {open && suggestions.length > 0 && (
        <ul style={dropdownStyle}>
          {suggestions
            .filter((suggestion) => suggestion.placePrediction)
            .map((suggestion) => (
              <li
                key={suggestion.placePrediction.placeId}
                style={itemStyle}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(suggestion);
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {suggestion.placePrediction.structuredFormat?.mainText?.text ||
                    suggestion.placePrediction.text.text}
                </div>
                <div style={{ fontSize: '13px', color: '#64748b' }}>
                  {suggestion.placePrediction.structuredFormat?.secondaryText?.text}
                </div>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

AddressSearchInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  required: PropTypes.bool,
  onBlur: PropTypes.func,
  onPlaceSelected: PropTypes.func,
};

export default AddressSearchInput;